import {
  ArrowRight,
  BookOpen,
  Camera,
  Menu,
  SearchCheck,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '../utils/cn';

const navLinks = [
  { icon: SearchCheck, label: 'Deals Finder', to: '/features/deals-finder' },
  { icon: Camera, label: 'AI Photography', to: '/features/ai-photography' },
  { icon: BookOpen, label: 'Blog', to: '/blog' },
] as const;

type GlobalNavbarProps = {
  className?: string;
};

export function GlobalNavbar({ className }: GlobalNavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className={cn('py-4', className)}>
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 rounded-[32px] border border-black/10 bg-[#f3f4f6] px-4 py-3 shadow-[0_14px_28px_-22px_rgba(15,23,42,0.45)] sm:px-5">
          <Link className="inline-flex min-w-0 items-center gap-2" to="/">
            <img
              alt="Profit Cars"
              className="h-12 w-auto object-contain"
              src="/logo.png"
            />
            <span className="truncate text-lg font-bold text-black sm:text-2xl">Profit Cars</span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-1.5 lg:flex">
            {navLinks.map((item) => (
              <NavLink
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition',
                    isActive
                      ? 'border-black/20 bg-white/85 text-black'
                      : 'border-transparent text-slate-700 hover:border-black/10 hover:bg-white/65 hover:text-black'
                  )
                }
                key={item.to}
                to={item.to}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <Link
            className="hidden h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-black/15 bg-white px-5 text-sm font-bold text-black transition hover:border-black/35 lg:inline-flex"
            to="/get"
          >
            Try App
            <ArrowRight className="h-4 w-4" />
          </Link>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/15 bg-white text-black transition hover:border-black/35"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              type="button"
            >
              {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <nav aria-label="Primary mobile" className="mt-3 rounded-[22px] border border-black/10 bg-white p-2.5 lg:hidden">
            <div className="grid gap-2">
              {navLinks.map((item) => (
                <NavLink
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-2 rounded-[12px] border px-3 py-2.5 text-sm font-semibold transition',
                      isActive
                        ? 'border-black/20 bg-[#f5f6f8] text-black'
                        : 'border-black/10 bg-white text-slate-700 hover:border-black/20 hover:bg-[#f7f8fa]'
                    )
                  }
                  key={item.to}
                  to={item.to}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
